import { API_BASE_URL } from '../config/env';
import {
  PULSE_COMMAND_GROUPS,
  PULSE_COMMAND_QUICK_ACTIONS,
  PULSE_CMD_FOCUS_PEDIDO_KEY,
  PULSE_CMD_CONV_UNREAD_KEY,
  PULSE_CMD_OPEN_WA_KEY,
  PULSE_CMD_WA_SEARCH_KEY
} from '../config/pulseCommand';
import { canUseCrmNavShortcut } from '../config/crmNavShortcuts';
import { OPEN_CONVERSATION_KEY } from '../components/ChatwootAssignmentToastListener';
import {
  buildConversationMessagePreview,
  formatWhatsappPhone,
  getCachedContactDisplayName,
  normalizeDigits,
  resolveConversationDisplayLabel
} from './chatwootContactDisplay';

const DEBOUNCE_MS = 280;

let pedidosCache = { at: 0, rows: [] };
const PEDIDOS_CACHE_MS = 45_000;

function defaultWaDateRange() {
  const hasta = new Date();
  const desde = new Date();
  desde.setDate(desde.getDate() - 30);
  const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { desde: iso(desde), hasta: iso(hasta) };
}

export function normalizeCommandQuery(raw) {
  return String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

export function tokenizeCommandQuery(q) {
  return normalizeCommandQuery(q)
    .split(/\s+/)
    .filter((t) => t.length >= 1);
}

function canAccess(user, { permission, adminOnly } = {}) {
  if (!user) return false;
  if (adminOnly) return Boolean(user.isAdmin);
  if (!permission) return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  return perms.includes(permission);
}

function scoreTokens(haystack, tokens) {
  if (!tokens.length) return 0;
  const h = normalizeCommandQuery(haystack);
  if (!h) return 0;
  let score = 0;
  for (const t of tokens) {
    if (h.includes(t)) score += 12;
    if (h.startsWith(t)) score += 8;
  }
  return score;
}

function buildResult(base) {
  return {
    id: base.id,
    group: base.group,
    title: base.title,
    subtitle: base.subtitle || '',
    icon: base.icon || '◇',
    score: base.score ?? 0,
    run: base.run
  };
}

function navResults(query, navItems, user, navigate) {
  const tokens = tokenizeCommandQuery(query);
  const out = [];
  for (const item of navItems || []) {
    if (!canAccess(user, { permission: item.permission, adminOnly: item.adminOnly })) continue;
    const label = `${item.label || ''} ${item.to || ''}`;
    const score = tokens.length ? scoreTokens(label, tokens) : 4;
    if (tokens.length && score < 8) continue;
    const path = item.to || '/';
    out.push(
      buildResult({
        id: `nav:${path}`,
        group: 'nav',
        title: item.label,
        subtitle: path,
        icon: item.icon || '◇',
        score: score + (tokens.length ? 0 : 1),
        run: () => navigate(path)
      })
    );
  }
  return out;
}

function shortcutResults(query, shortcuts, user, navigate) {
  const tokens = tokenizeCommandQuery(query);
  const out = [];
  for (const s of shortcuts || []) {
    if (!canUseCrmNavShortcut(user, s)) continue;
    const label = `${s.label} ${s.description || ''} ${s.path}`;
    const score = tokens.length ? scoreTokens(label, tokens) : 6;
    if (tokens.length && score < 10) continue;
    out.push(
      buildResult({
        id: `shortcut:${s.id}`,
        group: 'nav',
        title: s.label,
        subtitle: s.description || s.path,
        icon: s.icon || '◇',
        score: score + 20,
        run: () => navigate(s.path)
      })
    );
  }
  return out;
}

function quickActionResults(query, user, { unreadCount, navigate }) {
  const q = normalizeCommandQuery(query);
  const tokens = tokenizeCommandQuery(query);
  const out = [];

  for (const action of PULSE_COMMAND_QUICK_ACTIONS) {
    if (!canAccess(user, action)) continue;
    const matchesIntent = !q || action.intent.test(q) || scoreTokens(`${action.label} ${action.description}`, tokens) >= 12;
    if (!matchesIntent) continue;

    if (action.id === 'unread-conversations') {
      out.push(
        buildResult({
          id: action.id,
          group: 'action',
          title: action.label,
          subtitle:
            unreadCount > 0
              ? `${unreadCount} mensaje${unreadCount === 1 ? '' : 's'} sin leer`
              : 'No hay pendientes ahora',
          icon: action.icon,
          score: 200,
          run: () => {
            try {
              sessionStorage.setItem(PULSE_CMD_CONV_UNREAD_KEY, '1');
            } catch {
              // ignore
            }
            navigate('/conversaciones');
          }
        })
      );
      continue;
    }

    if (action.openWa) {
      out.push(
        buildResult({
          id: action.id,
          group: 'action',
          title: action.label,
          subtitle: 'Últimos 30 días · Contactos',
          icon: action.icon,
          score: 195,
          run: () => {
            try {
              sessionStorage.setItem(PULSE_CMD_OPEN_WA_KEY, '1');
              sessionStorage.removeItem(PULSE_CMD_WA_SEARCH_KEY);
            } catch {
              // ignore
            }
            navigate('/asociacion-clientes');
          }
        })
      );
    }
  }

  return out;
}

function conversationResults(query, conversations, { unreadOnly, navigate }) {
  const tokens = tokenizeCommandQuery(query);
  const list = Array.isArray(conversations) ? conversations : [];
  const filtered = unreadOnly
    ? list.filter((c) => Number(c?.unread_count || 0) > 0)
    : list;

  const scored = filtered
    .map((conv) => {
      const cached = getCachedContactDisplayName(conv?.sender?.phone_number);
      const title = resolveConversationDisplayLabel({ conversation: conv, cachedDisplayName: cached });
      const phone = formatWhatsappPhone(conv?.sender?.phone_number);
      const preview = buildConversationMessagePreview(conv);
      const haystack = `${title} ${phone} ${preview} ${conv.id}`;
      const score =
        (unreadOnly ? 40 : 0) +
        Number(conv.unread_count || 0) * 3 +
        scoreTokens(haystack, tokens);
      return { conv, title, phone, preview, score };
    })
    .filter((row) => (tokens.length ? row.score >= 14 : unreadOnly || row.score > 0))
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  return scored.map(({ conv, title, phone, preview, score }) =>
    buildResult({
      id: `conv:${conv.id}`,
      group: 'conversation',
      title,
      subtitle: preview || phone,
      icon: Number(conv.unread_count) > 0 ? '💬' : '💭',
      score,
      run: () => {
        try {
          sessionStorage.setItem(OPEN_CONVERSATION_KEY, String(conv.id));
        } catch {
          // ignore
        }
        navigate('/conversaciones');
      }
    })
  );
}

function contactRowLabel(row) {
  const ap = row?.apellido_geclisa || row?.apellido || '';
  const nom = row?.nombre_geclisa || row?.nombre_plex || row?.nombre || '';
  const name = [nom, ap].filter(Boolean).join(' ').trim() || row?.nombre_plex || 'Sin nombre';
  const dni = String(row?.dni_normalizado || row?.dni || '').replace(/\D/g, '');
  return { name, dni };
}

async function fetchAsociacionSearch(term, signal) {
  const res = await fetch(
    `${API_BASE_URL}/api/asociacion?page=1&limit=12&search=${encodeURIComponent(term)}`,
    { credentials: 'include', signal }
  );
  if (!res.ok) return [];
  const json = await res.json();
  return Array.isArray(json?.data) ? json.data : [];
}

async function fetchWaAltas(term, signal) {
  const { desde, hasta } = defaultWaDateRange();
  const res = await fetch(
    `${API_BASE_URL}/api/asociacion/altas-whatsapp?page=1&limit=8&desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}&search=${encodeURIComponent(term)}`,
    { credentials: 'include', signal }
  );
  if (!res.ok) return [];
  const json = await res.json();
  return Array.isArray(json?.data) ? json.data : Array.isArray(json?.rows) ? json.rows : [];
}

async function fetchPedidosCached(signal) {
  const now = Date.now();
  if (now - pedidosCache.at < PEDIDOS_CACHE_MS && pedidosCache.rows.length) {
    return pedidosCache.rows;
  }
  const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos`, {
    credentials: 'include',
    signal
  });
  if (!res.ok) return pedidosCache.rows;
  const json = await res.json();
  const raw = Array.isArray(json) ? json : Array.isArray(json?.pedidos) ? json.pedidos : [];
  pedidosCache = { at: now, rows: raw };
  return raw;
}

function pedidoResults(query, pedidos, navigate) {
  const tokens = tokenizeCommandQuery(query);
  if (!tokens.length) return [];

  const wantsPedido =
    tokens.some((t) => ['pedido', 'pendiente', 'ozempic', 'receta', 'aprobacion', 'medico'].includes(t)) ||
    tokens.length >= 2;

  if (!wantsPedido) return [];

  return (pedidos || [])
    .map((p) => {
      const med = [p.medicamento, p.medicamento_nombre, p.nombre_comercial, p.producto].filter(Boolean).join(' ');
      const haystack = `${p.paciente} ${p.paciente_dni} ${med} ${p.estado} ${p.id}`;
      const score = scoreTokens(haystack, tokens) + (p.estado === 'pendiente_aprobacion_medica' ? 15 : 0);
      return { p, med, score };
    })
    .filter((r) => r.score >= 14)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map(({ p, med, score }) =>
      buildResult({
        id: `pedido:${p.id}`,
        group: 'pedido',
        title: p.paciente || `Pedido #${p.id}`,
        subtitle: `${med || 'Medicamento'} · ${String(p.estado || '').replace(/_/g, ' ')}`,
        icon: '🩺',
        score,
        run: () => {
          try {
            sessionStorage.setItem(PULSE_CMD_FOCUS_PEDIDO_KEY, String(p.id));
          } catch {
            // ignore
          }
          navigate('/pedidos-recetas-medico');
        }
      })
    );
}

function waResults(query, rows, navigate) {
  const tokens = tokenizeCommandQuery(query);
  if (!tokens.length && !/wa|whatsapp|alta/.test(normalizeCommandQuery(query))) return [];

  return (rows || [])
    .map((row) => {
      const name = [row.nombre, row.apellido].filter(Boolean).join(' ').trim() || row.nombre_completo || 'Sin nombre';
      const dni = String(row.dni || row.dni_normalizado || '').replace(/\D/g, '');
      const tel = row.telefono || row.celular || '';
      const haystack = `${name} ${dni} ${tel}`;
      const score = scoreTokens(haystack, tokens) + 10;
      return { row, name, dni, score };
    })
    .filter((r) => r.score >= 12)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map(({ row, name, dni, score }) =>
      buildResult({
        id: `wa:${dni || row.id}`,
        group: 'wa',
        title: name,
        subtitle: dni ? `DNI ${dni} · Alta WhatsApp` : 'Alta WhatsApp',
        icon: '📱',
        score,
        run: () => {
          try {
            sessionStorage.setItem(PULSE_CMD_OPEN_WA_KEY, '1');
            if (dni || name) {
              sessionStorage.setItem(PULSE_CMD_WA_SEARCH_KEY, dni || name);
            }
          } catch {
            // ignore
          }
          navigate('/asociacion-clientes');
        }
      })
    );
}

export function groupCommandResults(results) {
  const order = Object.keys(PULSE_COMMAND_GROUPS);
  const byGroup = new Map();
  for (const r of results) {
    if (!byGroup.has(r.group)) byGroup.set(r.group, []);
    byGroup.get(r.group).push(r);
  }
  return order
    .filter((g) => byGroup.has(g))
    .map((g) => ({
      key: g,
      label: PULSE_COMMAND_GROUPS[g],
      items: byGroup.get(g).sort((a, b) => b.score - a.score)
    }));
}

export async function searchPulseCommand({
  query,
  user,
  navigate,
  navItems = [],
  shortcuts = [],
  conversations = [],
  unreadCount = 0,
  signal
}) {
  const q = String(query || '').trim();
  const tokens = tokenizeCommandQuery(q);
  const unreadOnly =
    /sin\s*leer|no\s*le[ií]d|unread/.test(normalizeCommandQuery(q)) ||
    PULSE_COMMAND_QUICK_ACTIONS[0].intent.test(normalizeCommandQuery(q));

  const sync = [
    ...quickActionResults(q, user, { unreadCount, navigate }),
    ...shortcutResults(q, shortcuts, user, navigate),
    ...navResults(q, navItems, user, navigate)
  ];

  if (!q) {
    return groupCommandResults(sync.filter((r) => r.group === 'action' || r.group === 'nav').slice(0, 12));
  }

  const asyncParts = [];

  if (canAccess(user, { permission: 'ver_conversaciones' })) {
    asyncParts.push(
      Promise.resolve(conversationResults(q, conversations, { unreadOnly, navigate }))
    );
  }

  const searchTerm = q.length >= 2 ? q : '';
  const phoneDigits = normalizeDigits(q);

  if (canAccess(user, { permission: 'ver_contactos' }) && (searchTerm || phoneDigits.length >= 6)) {
    asyncParts.push(
      fetchAsociacionSearch(searchTerm || phoneDigits, signal).then((rows) =>
        rows.map((row) => {
          const { name, dni } = contactRowLabel(row);
          return buildResult({
            id: `contact:${row.codcliente || dni}`,
            group: 'contact',
            title: name,
            subtitle: dni ? `DNI ${dni} · Ficha contacto` : 'Ficha contacto',
            icon: '👤',
            score: 50 + scoreTokens(`${name} ${dni}`, tokens),
            run: () => {
              const path = dni
                ? `/asociacion-clientes?dni=${encodeURIComponent(dni)}`
                : '/asociacion-clientes';
              navigate(path);
            }
          });
        })
      )
    );
  }

  if (canAccess(user, { permission: 'ver_pedido_recetas_medico' }) && tokens.length >= 2) {
    asyncParts.push(
      fetchPedidosCached(signal).then((pedidos) => pedidoResults(q, pedidos, navigate))
    );
  }

  if (
    canAccess(user, { permission: 'ver_contactos' }) &&
    (searchTerm || /wa|whatsapp|alta/.test(normalizeCommandQuery(q)))
  ) {
    asyncParts.push(
      fetchWaAltas(searchTerm || q, signal).then((rows) => waResults(q, rows, navigate))
    );
  }

  const asyncFlat = (await Promise.all(asyncParts)).flat();
  const merged = [...sync, ...asyncFlat];
  const deduped = [];
  const seen = new Set();
  for (const item of merged.sort((a, b) => b.score - a.score)) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    deduped.push(item);
  }

  return groupCommandResults(deduped.slice(0, 24));
}

export { DEBOUNCE_MS as PULSE_COMMAND_DEBOUNCE_MS };
